import React from 'react';
import { CheckCircle, Award, Ship, Headphones, Truck, HeartHandshake, BadgeCheck } from 'lucide-react';
import AnimatedSection from '../components/common/AnimatedSection';
import SEOHead from '../components/common/SEOHead';
import { companyInfo, aboutContent, differentials, heroImages } from '../data/mock';

const iconMap = {
  Award,
  Ship,
  HeadphonesIcon: Headphones,
  Truck,
  HeartHandshake,
  BadgeCheck
};

const CompanyPage = () => {
  return (
    <>
      <SEOHead 
        title="Sobre a Empresa"
        description={`Conheça a ${companyInfo.name}. ${aboutContent.fullDescription}`}
        keywords="Paulare Empilhadeiras, sobre nós, história, missão, visão, valores"
      />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-teal-900/30 py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-teal-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-teal-600 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <AnimatedSection animation="fadeUp">
            <span className="inline-flex items-center gap-2 bg-teal-500/20 text-teal-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
              Desde {companyInfo.foundedYear}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Nossa História
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl">
              {aboutContent.shortDescription}
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* About Content */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection animation="fadeRight">
              <img 
                src={heroImages.company} 
                alt="Paulare Empilhadeiras"
                className="w-full rounded-3xl shadow-2xl"
              />
            </AnimatedSection>
            
            <AnimatedSection animation="fadeLeft">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Tradição e Excelência
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                {aboutContent.fullDescription}
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                {aboutContent.milestone}
              </p>
              
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center p-6 bg-gray-50 rounded-2xl">
                  <div className="text-4xl font-bold text-teal-500 mb-2">{companyInfo.experienceYears}+</div>
                  <div className="text-gray-600">Anos de Experiência</div>
                </div>
                <div className="text-center p-6 bg-gray-50 rounded-2xl">
                  <div className="text-4xl font-bold text-teal-500 mb-2">1000+</div>
                  <div className="text-gray-600">Clientes Atendidos</div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <AnimatedSection animation="fadeUp" className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">Missão, Visão e Valores</h2>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-8">
            <AnimatedSection animation="fadeUp" delay={0}>
              <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 h-full">
                <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center mb-6">
                  <Award size={32} className="text-teal-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Missão</h3>
                <p className="text-gray-600 leading-relaxed">{aboutContent.mission}</p>
              </div>
            </AnimatedSection>

            <AnimatedSection animation="fadeUp" delay={100}>
              <div className="bg-gradient-to-br from-teal-500 to-teal-600 p-8 rounded-2xl text-white h-full">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                  <Ship size={32} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4">Visão</h3>
                <p className="text-teal-100 leading-relaxed">{aboutContent.vision}</p>
              </div>
            </AnimatedSection>

            <AnimatedSection animation="fadeUp" delay={200}>
              <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 h-full">
                <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center mb-6">
                  <HeartHandshake size={32} className="text-teal-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Valores</h3>
                <div className="flex flex-wrap gap-2">
                  {aboutContent.values.map((value) => (
                    <span key={value} className="bg-teal-50 text-teal-700 px-3 py-1 rounded-full text-sm font-medium">
                      {value}
                    </span>
                  ))}
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <AnimatedSection animation="fadeUp" className="text-center mb-16">
            <span className="text-teal-500 font-semibold uppercase tracking-wider">O Que Fazemos</span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-2">Nossos Serviços</h2>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { title: 'Compra', description: 'Compra de empilhadeiras novas e usadas de todas as marcas e modelos.' },
              { title: 'Venda', description: 'Venda de empilhadeiras novas e usadas, além de acessórios.' },
              { title: 'Locação', description: 'Aluguel de empilhadeiras para otimização logística.' },
              { title: 'Terceirização', description: 'Terceirização do setor logístico com operadores treinados.' },
              { title: 'Reforma', description: 'Reforma e restauração completa de empilhadeiras.' },
              { title: 'Manutenção', description: 'Manutenção preventiva e corretiva para sua frota.' }
            ].map((service, index) => (
              <AnimatedSection key={service.title} animation="fadeUp" delay={index * 100}>
                <div className="flex gap-4 p-6 bg-gray-50 rounded-2xl hover:bg-teal-50 transition-colors duration-500">
                  <CheckCircle className="text-teal-500 flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">{service.title}</h3>
                    <p className="text-gray-600">{service.description}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Differentials */}
      <section className="py-24 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <AnimatedSection animation="fadeUp" className="text-center mb-16">
            <span className="text-teal-400 font-semibold uppercase tracking-wider">Por Que Nos Escolher</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-2">Nossos Diferenciais</h2>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {differentials.map((diff, index) => {
              const IconComponent = iconMap[diff.icon] || CheckCircle;
              return (
                <AnimatedSection key={diff.id} animation="fadeUp" delay={index * 100}>
                  <div className="p-8 bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700 hover:border-teal-500/50 transition-all duration-500">
                    <div className="w-14 h-14 bg-teal-500/20 rounded-xl flex items-center justify-center mb-6">
                      <IconComponent size={28} className="text-teal-400" />
                    </div>
                    <h3 className="text-xl font-bold mb-3">{diff.title}</h3>
                    <p className="text-gray-400">{diff.description}</p>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
};

export default CompanyPage;
